/**
 * ContactAdminPanel — the "QUESTIONS / OFFERS / GENERAL CONTACT" lane on
 * the submission intake page. Members get a compact Archive Dispatch
 * composer addressed server-side to the editorial desk (Owner identity is
 * never exposed); non-members get the sign-in / join path.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Radio, X } from "lucide-react";
import { toast } from "sonner";
import { useMemberAuth } from "../hooks/useMemberAuth";
import { dispatchContactAdmin } from "../lib/api";

export const ContactAdminPanel = () => {
  const { member, token } = useMemberAuth();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await dispatchContactAdmin(token, { subject: subject.trim() || undefined, body: body.trim() });
      setSent(true);
      toast.success("Dispatch sent to the editorial desk");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dispatch failed");
    } finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="contact-admin-btn"
        className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#E4DFD3] text-[#E4DFD3] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
        <Radio size={12} className="text-[#D92D20]" /> Contact Admin
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center px-4"
             onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-[#12110e] border border-[#2D2A26] p-5"
               onClick={(e) => e.stopPropagation()} data-testid="contact-admin-modal">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// EDITORIAL DESK</span>
              <button onClick={() => setOpen(false)} data-testid="contact-admin-close"
                className="ml-auto w-9 h-9 inline-flex items-center justify-center text-[#757065]"><X size={15} /></button>
            </div>

            {!member ? (
              <div data-testid="contact-admin-guest">
                <p className="text-sm text-[#A39E93] leading-relaxed mb-4">
                  Direct Archive Dispatch to the editorial desk is available to
                  Archive members. Sign in or join to open a secure channel —
                  no email addresses are exchanged.
                </p>
                <div className="flex gap-2">
                  <Link to="/auth/login" data-testid="contact-admin-signin"
                    className="border border-[#E4DFD3] text-[#E4DFD3] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">Sign in</Link>
                  <Link to="/auth/signup" data-testid="contact-admin-join"
                    className="border border-[#454038] text-[#A39E93] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">Join The Archive</Link>
                </div>
              </div>
            ) : sent ? (
              <div data-testid="contact-admin-sent">
                <p className="text-sm text-[#A39E93] leading-relaxed mb-4">
                  Dispatch transmitted. The thread now lives in your normal
                  Dispatch inbox, tagged <span className="text-[#CBB892]">EDITORIAL DESK</span>.
                </p>
                <Link to="/inbox" className="border border-[#5DD4B3] text-[#5DD4B3] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">Open Dispatch</Link>
              </div>
            ) : (
              <form onSubmit={send} className="space-y-3" data-testid="contact-admin-form">
                <p className="text-[12px] text-[#757065] leading-relaxed">
                  Questions, offers or general contact — routed through your
                  secure Archive Dispatch channel to the editorial desk.
                </p>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                  placeholder="Subject (optional)" data-testid="contact-admin-subject"
                  className="w-full bg-black border border-[#3A3630] text-[#E4DFD3] text-sm p-2.5 font-mono" />
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} required
                  maxLength={8000} placeholder="Your message…" data-testid="contact-admin-body"
                  className="w-full bg-black border border-[#3A3630] text-[#E4DFD3] text-sm p-2.5" />
                <button type="submit" disabled={sending || !body.trim()} data-testid="contact-admin-send"
                  className="inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                  {sending ? <Loader2 size={11} className="animate-spin" /> : <Radio size={11} />} Send dispatch
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ContactAdminPanel;
